// Toast System - Simple and Clean

export { default as Toast } from './Toast';
export { ToastProvider, default as ToastContext } from './ToastProvider';
export { useToast } from './helpers';
export { defaultToastConfig, mergeConfig, getDefaultIcon, TOAST_TYPES } from './toastConfig';

// Toast instance and duplicate tracking (Map stores message key -> toast ID)
let toastInstance = null;
const activeToastMessages = new Map();

export const setToastInstance = (instance) => {
  toastInstance = instance;
};

// Check and remove duplicate message if exists
const handleDuplicate = (message, type) => {
  const key = `${type}:${message}`;
  const existingToastId = activeToastMessages.get(key);
  
  if (existingToastId && toastInstance?.removeToast) {
    toastInstance.removeToast(existingToastId);
    activeToastMessages.delete(key);
  }
};

// Track message with its ID
const trackMessage = (message, type, toastId) => {
  const key = `${type}:${message}`;
  activeToastMessages.set(key, toastId);
};

// Remove message from tracking
const untrackMessage = (message, type) => {
  const key = `${type}:${message}`;
  activeToastMessages.delete(key);
};

// Create toast method with duplicate replacement
const createToastMethod = (methodName) => (message, options = {}) => {
  if (!toastInstance?.[methodName]) {
    console.warn(`Toast instance not ready. Call toast.${methodName} after ToastProvider mounts.`);
    return null;
  }
  
  // Remove old duplicate if exists
  handleDuplicate(message, methodName);
  
  // Show new toast
  const toastId = toastInstance[methodName](message, options);
  const duration = options.duration || 3000;
  
  // Track this toast
  trackMessage(message, methodName, toastId);
  
  // Auto-cleanup tracking after duration
  setTimeout(() => {
    untrackMessage(message, methodName);
  }, duration + 500);
  
  return toastId;
};

export const toast = {
  success: createToastMethod('success'),
  error: createToastMethod('error'),
  warning: createToastMethod('warning'),
  info: createToastMethod('info'),
  show: (message, type, options = {}) => {
    if (!toastInstance?.showToast) {
      console.warn('Toast instance not ready. Call toast.show after ToastProvider mounts.');
      return null;
    }
    
    // Remove old duplicate if exists
    handleDuplicate(message, type);
    
    // Show new toast
    const toastId = toastInstance.showToast(message, type, options);
    const duration = options.duration || 3000;
    
    // Track this toast
    trackMessage(message, type, toastId);
    
    // Auto-cleanup tracking after duration
    setTimeout(() => {
      untrackMessage(message, type);
    }, duration + 500);
    
    return toastId;
  },
  removeToast: (id) => {
    if (toastInstance?.removeToast) {
      return toastInstance.removeToast(id);
    }
  },
  clearAllToasts: () => {
    if (toastInstance?.clearAllToasts) {
      activeToastMessages.clear();
      return toastInstance.clearAllToasts();
    }
  },
};
